// US-072: client for the Create-PO-from-supplier column configuration.
//
// Reads/writes the create-view column scope (independent of the PO-detail
// template) via the backend endpoints backed by the `po_create_line_item_columns`
// UserConfigs key. HTTP is injected (defaulting to the real http helpers) so the
// module unit-tests without chain-loading SupplierModel / models/report.ts.

import { get as httpGet, put as httpPut } from '@/services/http';
import { REORDER_PARAM_COLUMNS } from '@/utils/purchase-order/reorderParamColumns';
import { defaultExistingColumnConfig } from '@/utils/purchase-order/createViewHeaders';
// Reuse the PO column-config contract (US-069) rather than redefining it, so the
// create-view scope can't silently drift from the PO-detail template's shapes.
import type { ColumnConfig, MetafieldColumnConfig } from '@/services/po-column-template';

export type { ColumnConfig, MetafieldColumnConfig };

export const CREATE_VIEW_COLUMNS_ENDPOINT = '/purchase_orders/config/create-view-columns';
export const CUSTOM_COLUMNS_ENDPOINT = '/purchase_orders/config/custom-columns';

export interface CustomColumnDef { label: string; enabled: boolean; order: number; }

export interface CreateViewColumnConfig {
  columns: Record<string, ColumnConfig>;
  metafieldColumns: Record<string, MetafieldColumnConfig>;
}

function isPlainMap(v: any): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Client-side render fallback mirroring the backend default: existing Create-PO
 * columns enabled, reorder-param columns present-but-disabled. Used to render
 * the table before the merchant's saved config loads (and if the fetch fails).
 * Returns a fresh object each call.
 */
export function defaultCreateViewConfig(): CreateViewColumnConfig {
  // Existing columns come from the single descriptor shared with the header
  // builder; reorder-param columns are added disabled.
  const columns: Record<string, ColumnConfig> = { ...defaultExistingColumnConfig() };
  REORDER_PARAM_COLUMNS.forEach((c, i) => {
    columns[c.id] = { enabled: false, order: 20 + i };
  });
  return { columns, metafieldColumns: {} };
}

/** Normalize a raw config payload into a well-formed CreateViewColumnConfig. */
export function shapeCreateViewConfig(raw: any): CreateViewColumnConfig {
  return {
    columns: isPlainMap(raw?.columns) ? raw.columns : {},
    metafieldColumns: isPlainMap(raw?.metafieldColumns) ? raw.metafieldColumns : {},
  };
}

interface GetDep { get: (url: string, params?: any) => Promise<any>; }
interface PutDep { put: (url: string, body?: any) => Promise<any>; }

/**
 * GET the merchant's create-view column config. On any fetch/parse failure this
 * falls back to `defaultCreateViewConfig()` so callers always receive a usable,
 * renderable config (the create table stays functional on a transient error).
 */
export async function getCreateViewConfig(deps: GetDep = { get: httpGet }): Promise<CreateViewColumnConfig> {
  try {
    const response = await deps.get(CREATE_VIEW_COLUMNS_ENDPOINT);
    return shapeCreateViewConfig(response?.data);
  } catch (e) {
    return defaultCreateViewConfig();
  }
}

/**
 * GET the merchant's product custom-column definitions, shaped into
 * `{ [columnId]: { label, enabled, order } }`. Returns `{}` on any failure so
 * the Create-PO screen degrades gracefully (no custom columns rather than an
 * error). HTTP injected for testability.
 */
export async function getCustomColumnDefs(deps: GetDep = { get: httpGet }): Promise<Record<string, CustomColumnDef>> {
  try {
    const response = await deps.get(CUSTOM_COLUMNS_ENDPOINT);
    const raw = response?.data?.columns || {};
    const defs: Record<string, CustomColumnDef> = {};
    Object.keys(raw).forEach((id) => {
      defs[id] = {
        label: raw[id]?.label || id,
        enabled: raw[id]?.enabled === true,
        order: typeof raw[id]?.order === 'number' ? raw[id].order : 0,
      };
    });
    return defs;
  } catch (e) {
    return {};
  }
}

/** PUT the create-view column config; returns the shaped persisted config. */
export async function saveCreateViewConfig(
  config: CreateViewColumnConfig,
  deps: PutDep = { put: httpPut },
): Promise<CreateViewColumnConfig> {
  const response = await deps.put(CREATE_VIEW_COLUMNS_ENDPOINT, config);
  return shapeCreateViewConfig(response?.data);
}
